"use client";

import {
  GraduationCap,
  UserPlus,
  Users,
  UtensilsCrossed,
  type LucideIcon,
} from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { AnimatedCounter } from "@/components/motion/AnimatedCounter";
import { MotionIcon } from "@/components/motion/MotionIcon";
import { TextScramble } from "@/components/motion/TextScramble";
import type { HomeImpactMetricId } from "@/content/home";
import { cn } from "@/lib/utils";

const ICONS: Record<HomeImpactMetricId, LucideIcon> = {
  lives: Users,
  meals: UtensilsCrossed,
  children: GraduationCap,
  volunteers: UserPlus,
};

function iconForMetric(id: string): LucideIcon {
  return ICONS[id as HomeImpactMetricId] ?? Users;
}

function formatImpactValue(value: number): string {
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(value);
}

const lightGlassBase =
  "rounded-2xl border border-[var(--charity-green)]/20 bg-white/90 shadow-sm backdrop-blur-sm dark:bg-[var(--charity-surface-elevated)]/90";

interface ImpactMetricCardProps {
  metric: {
    id: string;
    label: string;
    value: number;
    suffix: string;
    featured?: boolean;
  };
  index?: number;
  variant?: "default" | "featured" | "compact";
}

export function ImpactMetricCard({
  metric,
  index = 0,
  variant = "default",
}: ImpactMetricCardProps) {
  const reduced = useReducedMotion();
  const Icon = iconForMetric(metric.id);

  if (variant === "featured") {
    return (
      <motion.div
        whileHover={reduced ? undefined : { y: -4 }}
        transition={{ type: "spring", stiffness: 400, damping: 24 }}
        className={cn(
          lightGlassBase,
          "home-impact-showcase__featured h-full p-8 md:p-10"
        )}
      >
        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--charity-green)]/10 text-[var(--charity-green-dark)]">
          <MotionIcon
            icon={Icon}
            size={28}
            pulseOnView
            hoverScale={1.1}
            className="text-[var(--charity-green-dark)]"
          />
        </div>
        <p className="font-display text-4xl font-bold text-[var(--charity-gold-dark)] md:text-5xl lg:text-6xl dark:text-[var(--charity-gold)]">
          <AnimatedCounter
            value={metric.value}
            formatter={formatImpactValue}
          />
          {metric.suffix}
        </p>
        <p className="mt-3 text-base font-medium text-muted md:text-lg">
          <TextScramble text={metric.label} as="span" duration={0.65} />
        </p>
      </motion.div>
    );
  }

  if (variant === "compact") {
    return (
      <motion.div
        whileHover={reduced ? undefined : { y: -4 }}
        transition={{ type: "spring", stiffness: 400, damping: 24 }}
        className={cn(lightGlassBase, "h-full px-4 py-4 md:px-5 md:py-5")}
      >
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--charity-green)]/10 text-[var(--charity-green-dark)]">
            <MotionIcon
              icon={Icon}
              size={18}
              pulseOnView
              hoverScale={1.1}
              className="text-[var(--charity-green-dark)]"
            />
          </span>
          <div className="min-w-0 text-left">
            <p className="text-xl font-bold leading-tight text-[var(--charity-green-dark)] md:text-2xl dark:text-[var(--charity-gold)]">
              <AnimatedCounter
                value={metric.value}
                formatter={formatImpactValue}
              />
              {metric.suffix}
            </p>
            <p className="mt-0.5 text-xs font-medium text-muted md:text-sm">
              <TextScramble text={metric.label} as="span" duration={0.65} />
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  // default (legacy light glass card)
  const floatDelays = ["0s", "0.4s", "0.8s", "1.2s"] as const;
  const floatDelay = floatDelays[index % floatDelays.length];

  return (
    <motion.div
      whileHover={reduced ? undefined : { y: -6 }}
      transition={{ type: "spring", stiffness: 400, damping: 24 }}
      className={cn(
        "impact-metric-card h-full px-4 py-6 text-center md:px-5 md:py-7",
        !reduced && "impact-metric-card--float"
      )}
      style={!reduced ? { animationDelay: floatDelay } : undefined}
    >
      <div className="mb-3 flex justify-center">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--programs-green)]/10 text-[var(--programs-green-dark)] dark:bg-[var(--charity-green)]/20 dark:text-[var(--charity-gold)]">
          <MotionIcon
            icon={Icon}
            size={22}
            pulseOnView
            hoverScale={1.15}
            className="text-[var(--charity-green-dark)] dark:text-[var(--charity-gold)]"
          />
        </span>
      </div>
      <p className="text-2xl font-bold text-[var(--charity-gold-dark)] dark:text-[var(--charity-gold)] md:text-3xl">
        <AnimatedCounter
          value={metric.value}
          formatter={formatImpactValue}
        />
        {metric.suffix}
      </p>
      <p className="mt-2 text-sm font-medium text-muted">
        <TextScramble text={metric.label} as="span" duration={0.65} />
      </p>
    </motion.div>
  );
}
